// Combat fallbacks. Item data lives in src/data/items.ts (owner: loot); these tables are used
// only when an entry there is missing, so combat never breaks while data is incomplete.
// The flag tables mirror docs/ARCHITECTURE.md "Behaviour flags" (binding) and are unioned with
// whatever flags the data declares. Pure data: no Phaser import.

import { TEX } from '../../art/keys';
import { COLORS } from '../../config/palette';
import type {
  EquipId,
  ItemEffect,
  MajorUpgradeId,
  ModId,
  MutationId,
  StatusApplication,
  SynergyDef,
  SynergyId,
  TrailKind,
  WeaponDef,
  WeaponId,
  WeaponModifier,
} from '../../core/types';

export const FALLBACK_WEAPONS: Readonly<Record<WeaponId, WeaponDef>> = {
  pulse_carbine: {
    id: 'pulse_carbine',
    name: 'Pulse Carbine',
    short: 'Reliable automatic',
    desc: 'Steady stream of pulse rounds.',
    tags: ['kinetic'],
    behavior: 'auto',
    holdStyle: 'rifle',
    damage: 11,
    fireRate: 9,
    projectileSpeed: 1700,
    spread: 0.07,
    pellets: 1,
    range: 1150,
    pierce: 0,
    knockback: 70,
    heatPerShot: 0.045,
    coolRate: 0.62,
    coolDelay: 0.22,
    overheatTime: 1.3,
    projectile: { texture: TEX.projPulse, radius: 6, scale: 1, tint: 0x9ff6ff },
    muzzleColor: 0x9ff6ff,
    sfx: 'shoot_pulse',
    recoil: 0.24,
    shake: 0.0012,
    weight: 10,
  },
  scatterforge: {
    id: 'scatterforge',
    name: 'Scatterforge',
    short: 'Close-range shotgun',
    desc: 'A cone of molten pellets with brutal knockback.',
    tags: ['kinetic'],
    behavior: 'spread',
    holdStyle: 'heavy',
    damage: 8,
    fireRate: 1.65,
    projectileSpeed: 1550,
    spread: 0.4,
    pellets: 9,
    range: 560,
    pierce: 0,
    knockback: 300,
    heatPerShot: 0.16,
    coolRate: 0.7,
    coolDelay: 0.3,
    overheatTime: 1.5,
    projectile: { texture: TEX.projPellet, radius: 5, scale: 1, tint: 0xffd98a },
    muzzleColor: 0xffc46b,
    sfx: 'shoot_scatter',
    recoil: 0.9,
    shake: 0.007,
    weight: 9,
  },
  arc_lance: {
    id: 'arc_lance',
    name: 'Arc Lance',
    short: 'Chaining lightning',
    desc: 'A crackling arc that leaps between nearby enemies.',
    tags: ['electric', 'chain'],
    behavior: 'beam_chain',
    holdStyle: 'rifle',
    damage: 9,
    fireRate: 8,
    projectileSpeed: 0,
    spread: 0,
    pellets: 1,
    range: 480,
    pierce: 0,
    knockback: 30,
    heatPerShot: 0.032,
    coolRate: 0.55,
    coolDelay: 0.3,
    overheatTime: 1.5,
    chain: { count: 2, range: 230, falloff: 0.85 },
    projectile: { texture: TEX.bolt, radius: 4 },
    muzzleColor: COLORS.electric,
    sfx: 'shoot_arc',
    recoil: 0.08,
    shake: 0.0005,
    weight: 8,
  },
  rail_driver: {
    id: 'rail_driver',
    name: 'Rail Driver',
    short: 'Piercing charge shot',
    desc: 'Hold to charge a slug that punches through targets.',
    tags: ['precision', 'kinetic'],
    behavior: 'charge',
    holdStyle: 'heavy',
    damage: 125,
    fireRate: 1.4,
    projectileSpeed: 4200,
    spread: 0,
    pellets: 1,
    range: 2100,
    pierce: 1,
    knockback: 420,
    heatPerShot: 0.4,
    coolRate: 0.55,
    coolDelay: 0.4,
    overheatTime: 1.7,
    chargeTime: 0.8,
    projectile: { texture: TEX.projRail, radius: 7, scale: 1, tint: 0xffb3b3 },
    muzzleColor: 0xff6b6b,
    sfx: 'shoot_rail',
    recoil: 1,
    shake: 0.009,
    weight: 8,
  },
  micro_rockets: {
    id: 'micro_rockets',
    name: 'Micro-Rocket Rack',
    short: 'Tracking missiles',
    desc: 'Each pull launches a fan of homing micro-missiles.',
    tags: ['explosive', 'missile'],
    behavior: 'missile_burst',
    holdStyle: 'heavy',
    damage: 14,
    fireRate: 1.15,
    projectileSpeed: 980,
    spread: 0.75,
    pellets: 1,
    range: 1300,
    pierce: 0,
    knockback: 150,
    heatPerShot: 0.21,
    coolRate: 0.5,
    coolDelay: 0.4,
    overheatTime: 1.7,
    burst: { count: 4, interval: 0.075 },
    explode: { radius: 72, damageMult: 0.85 },
    homing: { turnRate: 6.5, range: 700, delay: 0.1 },
    projectile: { texture: TEX.projRocket, radius: 7, scale: 1, tint: 0xffe0c0 },
    muzzleColor: COLORS.missile,
    sfx: 'shoot_rocket',
    recoil: 0.42,
    shake: 0.0024,
    weight: 8,
  },
  gravity_launcher: {
    id: 'gravity_launcher',
    name: 'Gravity Launcher',
    short: 'Singularity grenades',
    desc: 'Lobbed grenade that bursts into a field pulling enemies together, then implodes.',
    tags: ['gravity', 'explosive'],
    behavior: 'lobber',
    holdStyle: 'heavy',
    damage: 20,
    fireRate: 0.8,
    projectileSpeed: 1080,
    spread: 0,
    pellets: 1,
    range: 980,
    pierce: 0,
    knockback: 60,
    heatPerShot: 0.33,
    coolRate: 0.45,
    coolDelay: 0.5,
    overheatTime: 1.9,
    gravity: 1500,
    field: { radius: 180, duration: 2.4, strength: 2600, implodeDamageMult: 2.4 },
    explode: { radius: 70, damageMult: 0.6 },
    projectile: { texture: TEX.projGravity, radius: 11, scale: 1, tint: 0xd2b8ff },
    muzzleColor: COLORS.gravity,
    sfx: 'shoot_gravity',
    recoil: 0.62,
    shake: 0.0042,
    weight: 6,
    unlock: 'weapon:gravity_launcher',
  },
};

export const FALLBACK_MODS: Readonly<Record<ModId, WeaponModifier>> = {
  ionized: { addTags: ['electric'], addStatuses: [{ id: 'shock', stacks: 1, duration: 2.5 }], damageMult: 0.95 },
  incendiary: { addTags: ['burn'], addStatuses: [{ id: 'burn', stacks: 1, duration: 3, potency: 5 }], heatMult: 1.1 },
  fragmenting: { addTags: ['explosive'], explodeOnKill: { radius: 80, damageMult: 1.2 } },
  overclocked: { fireRateMult: 1.3, heatMult: 1.35 },
  cryo_coil: { addStatuses: [{ id: 'slow', duration: 1.5, potency: 0.3 }], coolMult: 1.5, damageMult: 0.9 },
  split_shot: { extraProjectiles: 2, damageMult: 0.6, spreadAdd: 0.08 },
  burst_fire: { burst: { count: 3, interval: 0.055 }, fireRateMult: 0.5, heatMult: 1.5 },
  heavy_rounds: { damageMult: 1.35, fireRateMult: 0.8, knockbackMult: 1.8, projectileSpeedMult: 0.9, pierceAdd: 1 },
};

/** ARCHITECTURE flag table: mutation → flags. */
export const MUTATION_FLAGS: Readonly<Partial<Record<MutationId, readonly string[]>>> = {
  arc_echo: ['ARC_ECHO'],
  siege_lock: ['SIEGE_LOCK'],
  ghost_protocol: ['GHOST_PROTOCOL'],
  feedback_loop: ['FEEDBACK_LOOP'],
  chain_reactor: ['CHAIN_REACTOR'],
  meltdown_engine: ['MELTDOWN'],
  volatile_payload: ['VOLATILE_PAYLOAD'],
  phase_capacitor: ['PHASE_CAPACITOR'],
  berserk_servos: ['BERSERK'],
};

/** ARCHITECTURE flag table: major upgrade → flags. */
export const UPGRADE_FLAGS: Readonly<Partial<Record<MajorUpgradeId, readonly string[]>>> = {
  signature_evolve: ['SIGNATURE_EVOLVED'],
  mod_slot: ['MOD_SLOT'],
};

/** ARCHITECTURE flag table: synergy → flags. */
export const SYNERGY_FLAGS: Readonly<Partial<Record<SynergyId, readonly string[]>>> = {
  storm_runner: ['STORM_RUNNER'],
  walking_fortress: ['WALKING_FORTRESS'],
  gravity_furnace: ['GRAVITY_FURNACE'],
  wildfire: ['WILDFIRE'],
};

/**
 * Synergy definitions per the ARCHITECTURE table, used only when SYNERGIES[id] is missing.
 * Stat grants are applied by inventory.computeStats; combat runs the effects and flags.
 */
export const FALLBACK_SYNERGIES: Readonly<Record<SynergyId, SynergyDef>> = {
  storm_runner: {
    id: 'storm_runner',
    name: 'Storm Runner',
    desc: 'Arc Dash strikes twice and reaches further. Dashing releases a chain arc.',
    requires: { electric: 3, chain: 2 },
    flags: ['STORM_RUNNER'],
    effects: [{ trigger: 'onDash', action: { type: 'chain', count: 1, range: 240, damage: 18 } }],
    color: COLORS.electric,
  },
  walking_fortress: {
    id: 'walking_fortress',
    name: 'Walking Fortress',
    desc: 'Hold still for 0.5s: +25% damage dealt, -25% damage taken.',
    requires: { shield: 2, precision: 1 },
    flags: ['WALKING_FORTRESS'],
    color: COLORS.shield,
  },
  gravity_furnace: {
    id: 'gravity_furnace',
    name: 'Gravity Furnace',
    desc: 'Gravity fields set enemies ablaze. Overheated enemies explode on death.',
    requires: { gravity: 1, burn: 1, explosive: 1 },
    flags: ['GRAVITY_FURNACE'],
    color: COLORS.burn,
  },
  conductor: {
    id: 'conductor',
    name: 'Conductor',
    desc: 'Chain lightning jumps to one more target.',
    requires: { electric: 2 },
    color: COLORS.electric,
  },
  wildfire: {
    id: 'wildfire',
    name: 'Wildfire',
    desc: 'Burning enemies spread their flames on death. Burn damage +25%.',
    requires: { burn: 3 },
    flags: ['WILDFIRE'],
    color: COLORS.burn,
  },
  demolition: {
    id: 'demolition',
    name: 'Demolition',
    desc: 'Explosion radius +35%.',
    requires: { explosive: 3 },
    color: COLORS.explosive,
  },
  bulwark: {
    id: 'bulwark',
    name: 'Bulwark',
    desc: 'Shields recover sooner. A shield break releases a shockwave.',
    requires: { shield: 3 },
    effects: [{ trigger: 'onShieldBreak', action: { type: 'shockwave', radius: 230, damage: 30, knockback: 720 } }],
    color: COLORS.shield,
  },
  afterburner: {
    id: 'afterburner',
    name: 'Afterburner',
    desc: 'Dash cooldown -20%. Dashes leave a burning trail.',
    requires: { dash: 3 },
    effects: [{ trigger: 'onDashEnd', action: { type: 'trail', kind: 'burn', dps: 22, duration: 1.8 } }],
    color: COLORS.burn,
  },
};

/** Status a dash trail applies when the effect does not specify one. */
export const TRAIL_DEFAULT_STATUS: Readonly<Record<TrailKind, StatusApplication | undefined>> = {
  electric: { id: 'shock', stacks: 1, duration: 2.5 },
  burn: { id: 'burn', stacks: 1, duration: 3 },
  plasma: undefined,
  void: { id: 'slow', duration: 1, potency: 0.4 },
};

/**
 * Implicit gameplay trail for legs equipment that declares `dashTrail` but no `trail` effect,
 * so "burning trails behind every dash" works even if the data only sets the visual field.
 */
export function implicitTrailEffect(_equip: EquipId, kind: TrailKind): ItemEffect {
  return { trigger: 'onDashEnd', action: { type: 'trail', kind, dps: kind === 'plasma' ? 20 : 16, duration: 1.6 } };
}
